import React, { useState, useRef } from 'react';
import classes from '../../../assets/Global.module.css';
import accordionClasses from '../../../assets/Accordion.module.css';
import GoogleSearchBoxEstablishment from '../../../helpers/inputs/GoogleSearchBoxEstablishment';
import TextArea from '../../../assets/essentials/TextArea';
import Input from '../../../assets/essentials/Input';
import Create from '../../../helpers/error/Create';
import Error from '../../../helpers/error/Error';
import classesAssign from '../../assign/Assign.module.css';
import Select from '../../../assets/essentials/Select';
import StatesAndProvinces from '../../../helpers/options/StatesAndProvinces';
import TransportationCaller from '../../../api/internal/TransportationCaller';

/**
 * LocationForm - Unified location selection and creation component
 *
 * This component consolidates the functionality of the previous LocationFromForm and LocationFromBody
 * components into a single, flexible component that operates in two distinct modes.
 *
 * @component
 *
 * ## Background
 * Previously, there were two separate implementations:
 * - **LocationFromBody**: Used in list/array contexts (e.g., stop lists in load creation) where each
 *   location was managed via callback functions that updated parent state by index
 * - **LocationFromForm**: Used in standalone contexts where location state was managed via React props
 *
 * This unified component replaces both by supporting two operational modes through a discriminated
 * union pattern.
 *
 * ## Operating Modes
 *
 * ### Props Mode (`mode: 'props'`)
 * Used in standalone contexts where location information is managed by parent component via props.
 *
 * **Use case**: Single location forms, dialogs, or pages where the parent manages one location object.
 *
 * **Props**:
 * - `mode: 'props'` - Activates props mode
 * - `locationInfo: LocationInfo | null` - Current location data from parent
 * - `setLocationInfo: (info) => void` - Parent setter function (supports both direct and functional updates)
 *
 * **Behavior**:
 * - Location state lives in parent component
 * - Updates flow through `setLocationInfo` prop
 * - Supports functional updates: `setLocationInfo(prev => ({ ...prev, field: value }))`
 * - Parent has full control over location data lifecycle
 *
 * **Example**:
 * ```tsx
 * const [location, setLocation] = useState<LocationInfo | null>(null);
 *
 * <LocationForm
 *   mode="props"
 *   locationInfo={location}
 *   setLocationInfo={setLocation}
 * />
 * ```
 *
 * ### Callback Mode (`mode: 'callback'`)
 * Used in list/array contexts where locations are part of a larger collection (e.g., stop lists).
 *
 * **Use case**: Load creation with multiple stops, itinerary management, or any scenario where
 * multiple locations are rendered in a list and need to update parent state by index.
 *
 * **Props**:
 * - `mode: 'callback'` - Activates callback mode
 * - `UpdateParam: (value, attributeName, stopIndex) => void` - Parent callback for field updates
 * - `stopIndex: number` - Index of this location in the parent's list
 * - `stop: CreateStopParam` - Complete stop object containing locationObj and other stop data
 *
 * **Behavior**:
 * - Internal location state managed within component
 * - Updates sent to parent via `UpdateParam(value, 'locationObj', stopIndex)` callback
 * - Individual field updates call `UpdateParam` for each field
 * - Component maintains its own `locationParam` state synchronized with parent
 * - Wrapped in `React.Fragment` with `key={stopIndex}` for proper list rendering
 *
 * **Example**:
 * ```tsx
 * const handleStopUpdate = (value: any, attributeName: string, index: number) => {
 *   setStops(prev => {
 *     const updated = [...prev];
 *     updated[index][attributeName] = value;
 *     return updated;
 *   });
 * };
 *
 * {stops.map((stop, index) => (
 *   <LocationForm
 *     mode="callback"
 *     UpdateParam={handleStopUpdate}
 *     stopIndex={index}
 *     stop={stop}
 *   />
 * ))}
 * ```
 *
 * ## Key Features
 *
 * ### Dual Search Capability
 * - **Database Search**: Search existing locations in the system database (default mode)
 * - **Google Places Search**: Create new locations via Google Places API (toggle to create mode)
 *
 * ### State Filtering
 * - Filter database search results by US state/province
 * - Client-side filtering for improved UX
 * - Filter persists during search but clears when switching modes
 *
 * ### Location Lifecycle
 * 1. User searches existing locations OR creates new location via Google Places
 * 2. Selected/created location populates all address fields
 * 3. User can edit special instructions (always) and additional fields (create mode only)
 * 4. Location data updates parent state via appropriate mode mechanism
 *
 * ### Mode-Specific Behaviors
 *
 * **Props Mode**:
 * - Direct state updates via `setLocationInfo`
 * - Entire location object replaced on selection
 * - Functional updates supported for granular changes
 *
 * **Callback Mode**:
 * - Field-by-field updates via `UpdateParam`
 * - Internal state synchronized with parent
 * - Each field change triggers individual callback
 * - Matches behavior of original LocationFromBody for backward compatibility
 *
 * ## Implementation Notes
 *
 * ### Type Safety
 * - Uses discriminated union types to enforce correct prop combinations
 * - Type guards (`isPropsMode`, `isCallbackMode`) provide type narrowing
 * - TypeScript ensures compile-time safety for mode-specific code paths
 *
 * ### State Management
 * - Props mode: State in parent, updates via setter
 * - Callback mode: Internal state + parent synchronization via callback
 * - Search state managed internally in both modes
 *
 * ### Rendering Optimization
 * - Callback mode wraps content in `React.Fragment` with `key={stopIndex}`
 * - Ensures proper re-rendering in list contexts
 * - Props mode returns content directly (no wrapper needed)
 *
 * @param {LocationFormUnifiedProps} props - Discriminated union props (PropsPattern or CallbackPattern)
 * @returns {JSX.Element} Location form UI with search and creation capabilities
 */

interface LocationFormPropsPattern {
  mode: 'props';
  locationInfo: LocationInfo | null;
  setLocationInfo: (info: LocationInfo | null | ((prev: LocationInfo | null) => LocationInfo | null)) => void;
}

interface LocationFormCallbackPattern {
  mode: 'callback';
  UpdateParam: ((value: any, attributeName: string, stopIndex: number) => void) | Function;
  stopIndex: number;
  stop: CreateStopParam;
}

type LocationFormUnifiedProps = LocationFormPropsPattern | LocationFormCallbackPattern;

// Type guard functions
function isCallbackMode(props: LocationFormUnifiedProps): props is LocationFormCallbackPattern {
  return props.mode === 'callback';
}

function isPropsMode(props: LocationFormUnifiedProps): props is LocationFormPropsPattern {
  return props.mode === 'props';
}

//it's not great, but better than it was before -ian

function LocationForm(props: LocationFormUnifiedProps) {
  const MINIMUM_SEARCH_CHARS = 1;
  const [searchResults, setSearchResults] = useState([]);
  const [createLocation, setCreateLocation] = useState(false);
  const createLocationButtonRef = useRef(null);
  const [selectedState, setSelectedState] = useState<string>(''); // State filter

  // Internal state for callback mode
  const [internalLocation, setInternalLocation] = useState<LocationInfo | null>(
    isCallbackMode(props) ? props.stop?.locationObj || null : null
  );

  // Initialize locationParam for callback mode (similar to LocationBody)
  const [locationParam] = useState<LocationInfo>({
    locationId: null,
    ianaTimeZone: '',
    locationName: '',
    addressLineOne: '',
    addressLineTwo: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    phone: '',
    phoneExt: '',
    email: '',
    hours: '',
    directions: '',
    specialInstructions: '',
    website: '',
    searchResult: '',
  });

  // Get the current location info based on mode
  const locationInfo = isPropsMode(props)
    ? props.locationInfo
    : internalLocation;

  // Search value state - needed for callback mode, also works for props mode
  const [searchValue, setSearchValue] = useState(
    isCallbackMode(props) ? internalLocation?.searchResult : undefined
  );

  // Filter search results by selected state (client-side)
  const filteredResults = selectedState
    ? searchResults.filter(location => location.state === selectedState)
    : searchResults;

  // Unified UpdateLocation function that handles both patterns
  function UpdateLocation(newValue, attributeName) {
    if (newValue?.toString().trim().length !== 0) {
      if (isPropsMode(props)) {
        // Props pattern: update parent state directly using functional form to get latest state
        props.setLocationInfo(prev => ({
          ...prev,
          [attributeName]: newValue,
        }));
      } else {
        // Callback pattern: update internal state and call UpdateParam
        locationParam[attributeName] = newValue;
        setInternalLocation(locationParam);
        props.UpdateParam(locationParam, 'locationObj', props.stopIndex);
      }
    }
  }

  /*
     - Resetting the location object has to be handled differently for each case
     - The searchTerm in HandleDBSearch must be maintained between responses from the backend
     - Setting locationInfo to null will not rerender, so the user can keep typing
     - Creating a new location needs the page to rerender to clear the fields and prevent the user from sumbitting
    */
  function ResetLocation(renderLocationObj) {
    const newLocationObj = renderLocationObj ? {
      ianaTimeZone: '',
      locationName: '',
      addressLineOne: '',
      addressLineTwo: '',
      city: '',
      state: '',
      zip: '',
      country: '',
      phone: '',
      phoneExt: '',
      email: '',
      hours: '',
      directions: '',
      specialInstructions: '',
      website: '',
      searchResult: '',
    } : null;

    if (isPropsMode(props)) {
      // Props pattern: set locationInfo via parent setter
      props.setLocationInfo(newLocationObj);
    } else {
      // Callback pattern: update internal state and call UpdateParam
      setInternalLocation(newLocationObj);
      props.UpdateParam(newLocationObj, 'locationObj', props.stopIndex);
    }
  }

  // Search current location directory
  function HandleDBSearch(searchTerm) {
    // Update search value for callback mode
    if (isCallbackMode(props)) {
      setSearchValue(searchTerm);
    }

    if (filteredResults.length) {
      let searchLocationMatch = filteredResults.find(
        result => result.searchResult === searchTerm,
      );
      if (searchLocationMatch) {
        if (isPropsMode(props)) {
          // Props pattern: set entire location object directly
          props.setLocationInfo(searchLocationMatch);
        } else {
          // Callback pattern: update all fields individually (matches LocationBody behavior)
          UpdateLocation(searchLocationMatch?.addressLineOne, 'addressLineOne');
          UpdateLocation(searchLocationMatch?.addressLineTwo, 'addressLineTwo');
          UpdateLocation(searchLocationMatch?.searchResult, 'searchResult');
          UpdateLocation(searchLocationMatch?.locationName, 'locationName');
          UpdateLocation(searchLocationMatch?.city, 'city');
          UpdateLocation(searchLocationMatch?.state, 'state');
          UpdateLocation(searchLocationMatch?.zip, 'zip');
          UpdateLocation(searchLocationMatch?.country, 'country');
          UpdateLocation(searchLocationMatch?.specialInstructions, 'specialInstructions');
          props.UpdateParam(searchLocationMatch?.ianaTimeZone, 'ianaTimeZone', props.stopIndex);
          props.UpdateParam(searchLocationMatch?.locationId, 'locationId', props.stopIndex);
        }
      } else {
        ResetLocation(false);
      }
    } else ResetLocation(false);

    if (searchTerm.length >= MINIMUM_SEARCH_CHARS) {
      const encodedSearchString = encodeURIComponent(searchTerm);
      TransportationCaller.get(
        `/Location/SearchLocationByLocationName?searchTerm=${encodedSearchString}`,
      ).then(response => {
        if (!response || response.status === 204) {
          //let user continue typing
          return;
        }
        setSearchResults(response?.data);
      });
    }
  }

  // Extract address components from Google Place
  function HandleNewLocationCallBack(place) {
    const renderLocationObj = true;
    const {
      name,
      formatted_phone_number: phone,
      website,
      address_components,
    } = place;

    let addressLineOne = '';
    let streetNumber = '';
    let streetName = '';
    let city = '';
    let state = '';
    let zip = '';
    let country = '';
    let requiredFields = [];

    address_components.forEach(googleResult => {
      googleResult.types.forEach(type => {
        if (type === 'street_number') {
          streetNumber = googleResult.short_name;
        } else if (type === 'route') {
          streetName = googleResult.short_name;
        } else if (type === 'locality') {
          city = googleResult.short_name;
        } else if (type === 'administrative_area_level_1') {
          state = googleResult.short_name;
        } else if (type === 'postal_code') {
          zip = googleResult.short_name;
        } else if (type === 'country') {
          country = googleResult.short_name;
        }
      });
    });

    if (streetNumber && streetName) {
      addressLineOne = streetNumber + ' ' + streetName;
    }

    // Update search value for callback mode
    if (isCallbackMode(props)) {
      setSearchValue(place.name);
    }

    requiredFields.push(addressLineOne, city, state, zip, country);
    for (const field of requiredFields) {
      if (field.trim() === '') {
        ResetLocation(renderLocationObj);
        Create(
          <Error customMessage="Location does not contain all required fields. Please try a different location." />,
        );
        return;
      }
    }
    UpdateLocation(name, 'locationName');
    UpdateLocation(addressLineOne, 'addressLineOne');
    UpdateLocation(city, 'city');
    UpdateLocation(state, 'state');
    UpdateLocation(zip, 'zip');
    UpdateLocation(country, 'country');
    UpdateLocation(phone, 'phone');
    UpdateLocation(website, 'website');

    //Removes locationId when creating a new location
    if (isPropsMode(props)) {
      props.setLocationInfo(prev => ({ ...prev, locationId: null }));
    } else {
      props.UpdateParam(null, 'locationId', props.stopIndex);
    }
  }

  const AutoCompleteBehavior = createLocation ? (
    // If we are creating a location, we want the search box to search Google for location info
    <>
      <GoogleSearchBoxEstablishment
        assignPlace={HandleNewLocationCallBack}
        id="location"
      />
      <div className={classes.cTA}>
        <label className={classes.cTA}>
          {isCallbackMode(props) ? 'Search existing locations' : 'Add existing location'}
          <div className={classes.hiddenToggler}>
            <Input
              type="checkbox"
              defaultChecked={createLocation}
              onChange={() => {
                setCreateLocation(false);
                setSelectedState(''); // Clear state filter when switching to create mode
                if (isCallbackMode(props)) {
                  ResetLocation(true);
                }
              }}
              name="toggleNewLocation"
              className={classes.hiddenToggler}
              value={isCallbackMode(props) ? undefined : null}
            />
          </div>
        </label>
      </div>
    </>
  ) : (
    // If we are not creating a location, we will display the datalist that calls the backend
    <>
      {/* State/Province Filter */}
      <div className={classes.attribute}>
        <label> Filter State</label>
        <Select
          value={selectedState}
          onChange={e => setSelectedState(e.target.value)}
        >
          <option value="">All States</option>
          <StatesAndProvinces />
        </Select>
      </div>
      <label>Existing Location </label>
      <Input
        type="text"
        value={isCallbackMode(props) ? searchValue : locationInfo?.searchResult}
        onChange={e => HandleDBSearch(e.target.value)}
        placeholder="Search for saved locations..."
        list="optionsList"
        name="location"
        id="location"
        autoComplete="off"
      />
      <div
        className={`${classes.cTA}`}
        tabIndex={0}
        ref={createLocationButtonRef}
        onClick={e => {
          e.preventDefault();
          setCreateLocation(!createLocation);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            setCreateLocation(true);
          }
        }}
      >
        <label className={classes.cTA}>Add new location</label>
      </div>
      <datalist id="optionsList">
        {filteredResults?.map(location => (
          <option key={location.locationId} value={location.searchResult} />
        ))}
      </datalist>
    </>
  );

  // Editable fields that are only rendered when the createLocation variable is true
  const CreateLocationAdditionalFields = () => {
    return (
      <>
        <div
          className={`${classes.attribute} ${classes.span3} ${classesAssign.createLocation} ${accordionClasses.createLocation}`}
        >
          <label>Hours</label>
          <TextArea
            defaultValue={locationInfo?.hours}
            onChange={e => {
              UpdateLocation(e.target.value, 'hours');
            }}
          />
        </div>
        <div className={`${classes.attribute} ${classes.span3}`}>
          <label>Directions</label>
          <TextArea
            defaultValue={locationInfo?.directions}
            onChange={e => UpdateLocation(e.target.value, 'directions')}
            type="text"
          />
        </div>
      </>
    );
  };

  const content = (
    <>
      <div className={`${classes.attribute} ${classes.span3}`}>
        <strong>Location Information</strong>
      </div>
      <div className={`${classes.attribute} ${classes.span3}`}>
        {AutoCompleteBehavior}
      </div>
      <div className={`${classes.attribute} ${classes.span3}`}>
        <label>
          Name <span className={classes.required}>*</span>
        </label>
        <Input
          disabled
          value={locationInfo ? locationInfo?.locationName : ''}
        />
      </div>
      <div className={classes.attribute}>
        <label>
          Address Line One <span className={classes.required}>*</span>
        </label>
        <Input
          disabled
          value={locationInfo?.addressLineOne}
          onChange={e => UpdateLocation(e.target.value, 'addressLineOne')}
        />
      </div>
      <div className={`${classes.attribute} ${classes.span2}`}>
        <label>Address Line Two</label>
        <Input
          disabled
          value={locationInfo?.addressLineTwo}
          onChange={e => UpdateLocation(e.target.value, 'addressLineTwo')}
        />
      </div>
      <div className={classes.attribute}>
        <label>
          City <span className={classes.required}>*</span>
        </label>
        <Input
          disabled
          value={locationInfo?.city}
          onChange={e => UpdateLocation(e.target.value, 'city')}
        />
      </div>
      <div className={`${classes.attribute} ${classes.span2}`}>
        <label>
          State/Province <span className={classes.required}>*</span>
        </label>
        <Select
          disabled
          value={locationInfo?.state}
          onChange={e => UpdateLocation(e.target.value, 'state')}
        >
          <option value="" />
          <StatesAndProvinces />
        </Select>
      </div>
      <div className={classes.attribute}>
        <label>
          Zip <span className={classes.required}>*</span>
        </label>
        <Input
          disabled
          value={locationInfo?.zip}
          onChange={e => UpdateLocation(e.target.value, 'zip')}
        />
      </div>
      <div className={`${classes.attribute} ${classes.span2}`}>
        <label>
          Country <span className={classes.required}>*</span>
        </label>
        <Select
          disabled
          value={locationInfo?.country || ''}
          onChange={e => UpdateLocation(e.target.value, 'country')}
        >
          <option value="" />
          <option value="US">US</option>
          <option value="CA">Canada</option>
        </Select>
      </div>
      <div className={`${classes.attribute} ${classes.span3}`}>
        <label>Special Instructions</label>
        <TextArea
          value={isCallbackMode(props) ? undefined : locationInfo?.specialInstructions}
          defaultValue={isCallbackMode(props) ? locationInfo?.specialInstructions : undefined}
          disabled={createLocation ? false : true}
          onChange={e => UpdateLocation(e.target.value, 'specialInstructions')}
        />
      </div>
      {createLocation && <CreateLocationAdditionalFields />}
    </>
  );

  // Conditionally wrap in Fragment with key for callback mode (list rendering)
  if (isCallbackMode(props)) {
    return <React.Fragment key={props.stopIndex}>{content}</React.Fragment>;
  }

  return content;
}

export default LocationForm;
